import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useUsageAgentNames, useUsageUserNames } from 'features/usage/hooks/use-usage';
import { Button } from 'components/ui/button';
import { Card, CardContent } from 'components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from 'components/ui/dialog';
import { cn } from 'lib/utils';
import {
  TokenUsageAgentEntry,
  TokenUsageModelEntry,
  TokenUsageOperationEntry,
  TokenUsageProviderEntry,
  TokenUsageUserEntry,
} from 'features/usage/services/usage-service';
import { UsageDay } from 'features/usage/utils/build-usage-view-model';
import {
  DEFAULT_CHAT_AGENT_ID,
  formatNumber,
  formatShortDate,
  getAverage,
  getRatio,
  getUserLabel,
} from 'features/usage/utils/usage-format';
import { UsageDailyChart, UsageProviderChart, UsageShareChart } from './usage-charts';
import { UsageSectionLabel } from './usage-section-label';

const modelPalette = [
  'bg-primary-100 text-true-white border-primary-200',
  'bg-warning-background text-warning-high-emphasis border-warning/20',
  'bg-secondary-100 text-secondary-700 border-secondary-200',
  'bg-pink-100 text-pink-700 border-pink-200',
] as const;

export function UsageDailySection({ days, peakTokens }: { days: UsageDay[]; peakTokens: number }) {
  const { t } = useTranslation();
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);

  return (
    <div className="space-y-4">
      <UsageSectionLabel>{t('DAILY_USAGE')}</UsageSectionLabel>
      <Card className="border-border/70 shadow-none">
        <CardContent className="p-5 md:p-6">
          <div className="mb-5 flex items-start justify-between gap-3">
            <div>
              <div className="text-sm font-medium text-foreground">{t('DAILY_USAGE')}</div>
              <div className="text-xs text-muted-foreground">{t('DAILY_USAGE_SUBTITLE')}</div>
            </div>
            <Button variant="outline" size="sm" onClick={() => setIsDetailsOpen(true)}>
              {t('DETAILS')}
            </Button>
          </div>
          <UsageDailyChart days={days} />
        </CardContent>
      </Card>

      <Dialog open={isDetailsOpen} onOpenChange={setIsDetailsOpen}>
        <DialogContent className="!flex max-h-[85vh] max-w-5xl flex-col gap-0 overflow-hidden p-0">
          <DialogHeader className="shrink-0 border-b border-border px-6 py-5">
            <DialogTitle>{t('DAILY_ACTIVITY')}</DialogTitle>
            <DialogDescription className="sr-only">{t('DAILY_USAGE_SUBTITLE')}</DialogDescription>
          </DialogHeader>
          <div className="shrink-0 border-b border-border px-6">
            <table className="w-full table-fixed">
              <thead>
                <tr className="h-12">
                  <th className="align-middle pr-4 text-left text-xs font-medium text-muted-foreground">
                    {t('DATE')}
                  </th>
                  <th className="align-middle px-4 text-left text-xs font-medium text-muted-foreground">
                    {t('INPUT')}
                  </th>
                  <th className="align-middle px-4 text-left text-xs font-medium text-muted-foreground">
                    {t('OUTPUT')}
                  </th>
                  <th className="align-middle px-4 text-left text-xs font-medium text-muted-foreground">
                    {t('TOTAL')}
                  </th>
                  <th className="align-middle px-4 text-left text-xs font-medium text-muted-foreground">
                    {t('TURNS')}
                  </th>
                  <th className="align-middle pl-4 text-left text-xs font-medium text-muted-foreground">
                    {t('RATIO')}
                  </th>
                </tr>
              </thead>
            </table>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto px-6 pb-5">
            <table className="w-full table-fixed">
              <tbody>
                {days.map((day) => (
                  <tr
                    key={day.date}
                    className={cn('h-14 border-t border-border', day.inactive && 'opacity-35')}
                  >
                    <td className="align-middle py-3 pr-4 font-mono text-xs text-muted-foreground">
                      {formatShortDate(day.date)}
                      {!day.inactive && day.total_tokens === peakTokens && (
                        <div className="mt-1 text-[10px] uppercase tracking-[0.12em] text-muted-foreground">
                          {t('PEAK')}
                        </div>
                      )}
                    </td>
                    {day.inactive ? (
                      <td colSpan={5} className="align-middle px-4 py-3 text-sm text-muted-foreground">
                        {t('NO_ACTIVITY')}
                      </td>
                    ) : (
                      <>
                        <td className="align-middle px-4 py-3 font-mono text-xs text-primary dark:text-true-white">
                          {formatNumber(day.input_tokens)}
                          <div className="mt-1 text-[10px] text-muted-foreground">
                            {t('AVG_SHORT', { avg: getAverage(day.input_tokens, day.turn_count) })}
                          </div>
                        </td>
                        <td className="align-middle px-4 py-3 font-mono text-xs text-success-green">
                          {formatNumber(day.output_tokens)}
                          <div className="mt-1 text-[10px] text-muted-foreground">
                            {t('AVG_SHORT', { avg: getAverage(day.output_tokens, day.turn_count) })}
                          </div>
                        </td>
                        <td className="align-middle px-4 py-3 font-mono text-xs text-foreground">
                          {formatNumber(day.total_tokens)}
                        </td>
                        <td className="align-middle px-4 py-3 font-mono text-xs text-foreground">
                          {formatNumber(day.turn_count)}
                        </td>
                        <td className="align-middle py-3 pl-4 font-mono text-xs text-muted-foreground">
                          {getRatio(day.input_tokens, day.output_tokens)}
                        </td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export function UsageModelTable({
  models,
  totalTokens,
}: {
  models: TokenUsageModelEntry[];
  totalTokens: number;
}) {
  const { t } = useTranslation();

  return (
    <div className="space-y-4">
      <UsageSectionLabel>{t('ANALYTICS_PER_MODEL')}</UsageSectionLabel>
      <Card className="border-border/70 shadow-none">
        <CardContent className="p-5 md:p-6">
          <div className="max-h-[552px] overflow-auto">
            <table className="min-w-full border-collapse">
              <thead>
                <tr className="h-12 border-b border-border">
                  <th className="sticky top-0 z-10 align-middle bg-card pr-4 text-left text-xs font-medium text-muted-foreground">
                    {t('MODEL')}
                  </th>
                  <th className="sticky top-0 z-10 align-middle bg-card px-4 text-left text-xs font-medium text-muted-foreground">
                    {t('MODEL_CALLS')}
                  </th>
                  <th className="sticky top-0 z-10 align-middle bg-card px-4 text-left text-xs font-medium text-muted-foreground">
                    {t('INPUT_TOKENS')}
                  </th>
                  <th className="sticky top-0 z-10 align-middle bg-card px-4 text-left text-xs font-medium text-muted-foreground">
                    {t('OUTPUT_TOKENS')}
                  </th>
                  <th className="sticky top-0 z-10 align-middle bg-card px-4 text-left text-xs font-medium text-muted-foreground">
                    {t('TOTAL_TOKENS')}
                  </th>
                  <th className="sticky top-0 z-10 align-middle bg-card px-4 text-left text-xs font-medium text-muted-foreground">
                    {t('RATIO')}
                  </th>
                  <th className="sticky top-0 z-10 align-middle bg-card pl-4 text-left text-xs font-medium text-muted-foreground">
                    {t('SHARE')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {models.map((model, index) => {
                  const share = totalTokens ? (model.total_tokens / totalTokens) * 100 : 0;
                  const badgeClassName =
                    model.model === 'unknown'
                      ? 'border-border bg-muted text-muted-foreground'
                      : modelPalette[index % modelPalette.length];

                  return (
                    <tr
                      key={model.model}
                      className={cn('border-t border-border', model.model === 'unknown' && 'opacity-60')}
                    >
                      <td className="py-3 pr-4">
                        <span
                          className={cn(
                            'inline-flex rounded-full border px-3 py-1 font-mono text-[10px] uppercase tracking-[0.12em] dark:text-true-white',
                            badgeClassName
                          )}
                        >
                          {model.model}
                        </span>
                      </td>
                      <td className="px-4 py-3 font-mono text-xs text-foreground">
                        {formatNumber(model.model_call_count)}
                        <div className="mt-1 text-[10px] text-muted-foreground">
                          {t('AVG_TOK', { avg: getAverage(model.total_tokens, model.model_call_count) })}
                        </div>
                      </td>
                      <td className="px-4 py-3 font-mono text-xs text-primary dark:text-true-white">
                        {formatNumber(model.input_tokens)}
                        <div className="mt-1 text-[10px] text-muted-foreground">
                          {t('AVG_SHORT', { avg: getAverage(model.input_tokens, model.model_call_count) })}
                        </div>
                      </td>
                      <td className="px-4 py-3 font-mono text-xs text-success-green">
                        {formatNumber(model.output_tokens)}
                        <div className="mt-1 text-[10px] text-muted-foreground">
                          {t('AVG_SHORT', { avg: getAverage(model.output_tokens, model.model_call_count) })}
                        </div>
                      </td>
                      <td className="px-4 py-3 font-mono text-xs text-foreground">
                        {formatNumber(model.total_tokens)}
                      </td>
                      <td className="px-4 py-3 font-mono text-xs text-foreground">
                        {getRatio(model.input_tokens, model.output_tokens)}
                      </td>
                      <td className="py-3 pl-4">
                        <div
                          className={cn(
                            'font-mono text-lg',
                            model.model === 'unknown'
                              ? 'text-muted-foreground'
                              : 'text-warning-high-emphasis'
                          )}
                        >
                          {share.toFixed(1)}%
                        </div>
                        <div className="mt-1 font-mono text-[10px] text-muted-foreground">
                          {t('TOK_SHORT', { value: formatNumber(model.total_tokens) })}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

const operationLabelKeys: Record<string, string> = {
  conversation: 'OPERATION_CONVERSATION',
  title_generation: 'OPERATION_TITLE_GENERATION',
  self_improvement_review: 'OPERATION_SELF_IMPROVEMENT',
  image_generation: 'OPERATION_IMAGE_GENERATION',
  model_routing: 'OPERATION_MODEL_ROUTING',
};

export function UsageOperationSection({
  operations,
  totalTokens,
}: {
  operations: TokenUsageOperationEntry[];
  totalTokens: number;
}) {
  const { t } = useTranslation();
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  if (!operations.length) return null;

  return (
    <div className="flex h-full flex-col gap-4">
      <UsageSectionLabel>{t('BY_OPERATION')}</UsageSectionLabel>
      <Card className="flex-1 border-border/70 shadow-none">
        <CardContent className="flex h-full flex-col p-5 md:p-6">
          <div className="mb-5 flex min-h-8 items-center justify-between gap-3">
            <div className="text-xs font-medium text-muted-foreground">{t('TOKEN_SHARE')}</div>
            <Button variant="outline" size="sm" onClick={() => setIsDetailsOpen(true)}>
              {t('DETAILS')}
            </Button>
          </div>
          <UsageShareChart
            items={operations.map((operation) => ({
              name: t(operationLabelKeys[operation.operation] ?? operation.operation),
              value: operation.total_tokens,
            }))}
          />
        </CardContent>
      </Card>

      <Dialog open={isDetailsOpen} onOpenChange={setIsDetailsOpen}>
        <DialogContent className="!flex max-h-[85vh] w-[min(96vw,80rem)] !max-w-7xl flex-col gap-0 overflow-hidden p-0">
          <DialogHeader className="shrink-0 border-b border-border px-6 py-5">
            <DialogTitle>{t('BY_OPERATION')}</DialogTitle>
            <DialogDescription className="sr-only">{t('TOKEN_SHARE')}</DialogDescription>
          </DialogHeader>
          <div className="shrink-0 border-b border-border px-6">
            <table className="w-full table-fixed">
              <colgroup>
                <col className="w-[32%]" />
                <col className="w-[13%]" />
                <col className="w-[15%]" />
                <col className="w-[15%]" />
                <col className="w-[15%]" />
                <col className="w-[10%]" />
              </colgroup>
              <thead>
                <tr className="h-12">
                  <th className="align-middle pr-4 text-left text-xs font-medium text-muted-foreground">
                    {t('OPERATION')}
                  </th>
                  <th className="align-middle px-4 text-left text-xs font-medium text-muted-foreground">
                    {t('MODEL_CALLS')}
                  </th>
                  <th className="align-middle px-4 text-left text-xs font-medium text-muted-foreground">
                    {t('INPUT_TOKENS')}
                  </th>
                  <th className="align-middle px-4 text-left text-xs font-medium text-muted-foreground">
                    {t('OUTPUT_TOKENS')}
                  </th>
                  <th className="align-middle px-4 text-left text-xs font-medium text-muted-foreground">
                    {t('TOTAL_TOKENS')}
                  </th>
                  <th className="align-middle pl-4 text-left text-xs font-medium text-muted-foreground">
                    {t('SHARE')}
                  </th>
                </tr>
              </thead>
            </table>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto px-6 pb-5">
            <table className="w-full table-fixed">
              <colgroup>
                <col className="w-[32%]" />
                <col className="w-[13%]" />
                <col className="w-[15%]" />
                <col className="w-[15%]" />
                <col className="w-[15%]" />
                <col className="w-[10%]" />
              </colgroup>
              <tbody>
                {operations.map((operation, index) => {
                  const share = totalTokens ? (operation.total_tokens / totalTokens) * 100 : 0;
                  return (
                    <tr key={operation.operation} className="border-t border-border">
                      <td className="py-3 pr-4">
                        <span
                          className={cn(
                            'inline-flex rounded-full border px-3 py-1 font-mono text-[10px] uppercase tracking-[0.12em] dark:text-true-white',
                            modelPalette[index % modelPalette.length]
                          )}
                        >
                          {t(operationLabelKeys[operation.operation] ?? operation.operation)}
                        </span>
                      </td>
                      <td className="px-4 py-3 font-mono text-xs text-foreground">
                        {formatNumber(operation.model_call_count)}
                      </td>
                      <td className="px-4 py-3 font-mono text-xs text-primary dark:text-true-white">
                        {formatNumber(operation.input_tokens)}
                      </td>
                      <td className="px-4 py-3 font-mono text-xs text-success-green">
                        {formatNumber(operation.output_tokens)}
                      </td>
                      <td className="px-4 py-3 font-mono text-xs text-foreground">
                        {formatNumber(operation.total_tokens)}
                      </td>
                      <td className="py-3 pl-4 font-mono text-lg text-warning-high-emphasis">
                        {share.toFixed(1)}%
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export function UsageProviderSection({ providers }: { providers: TokenUsageProviderEntry[] }) {
  const { t } = useTranslation();
  if (!providers.length) return null;

  return (
    <div className="flex h-full flex-col gap-4">
      <UsageSectionLabel>{t('BY_PROVIDER')}</UsageSectionLabel>
      <Card className="flex-1 border-border/70 shadow-none">
        <CardContent className="flex h-full flex-col p-5 md:p-6">
          <div className="mb-5 flex min-h-8 items-center">
            <div className="text-xs font-medium text-muted-foreground">{t('MODEL_CALL_SHARE')}</div>
          </div>
          <UsageProviderChart providers={providers} />
        </CardContent>
      </Card>
    </div>
  );
}

export function UsageAgentTable({
  agents,
  totalTokens,
}: {
  agents: TokenUsageAgentEntry[];
  totalTokens: number;
}) {
  const { t } = useTranslation();
  const agentIds = useMemo(
    () => agents.map((agent) => agent.agent_id).filter((agentId): agentId is string => !!agentId),
    [agents]
  );
  const agentNames = useUsageAgentNames(agentIds);

  if (!agents.length) return null;

  return (
    <div className="space-y-4">
      <UsageSectionLabel>{t('TOP_AGENTS')}</UsageSectionLabel>
      <Card className="border-border/70 shadow-none">
        <CardContent className="p-5 md:p-6">
          <div className="max-h-[552px] overflow-auto">
            <table className="min-w-full border-collapse">
              <thead>
                <tr className="h-12 border-b border-border">
                  <th className="sticky top-0 z-10 align-middle bg-card pr-4 text-left text-xs font-medium text-muted-foreground">
                    {t('AGENT')}
                  </th>
                  <th className="sticky top-0 z-10 align-middle bg-card px-4 text-left text-xs font-medium text-muted-foreground">
                    {t('TURNS')}
                  </th>
                  <th className="sticky top-0 z-10 align-middle bg-card px-4 text-left text-xs font-medium text-muted-foreground">
                    {t('INPUT_TOKENS')}
                  </th>
                  <th className="sticky top-0 z-10 align-middle bg-card px-4 text-left text-xs font-medium text-muted-foreground">
                    {t('OUTPUT_TOKENS')}
                  </th>
                  <th className="sticky top-0 z-10 align-middle bg-card px-4 text-left text-xs font-medium text-muted-foreground">
                    {t('TOTAL_TOKENS')}
                  </th>
                  <th className="sticky top-0 z-10 align-middle bg-card pl-4 text-left text-xs font-medium text-muted-foreground">
                    {t('SHARE')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {agents.map((agent, index) => {
                  const share = totalTokens ? (agent.total_tokens / totalTokens) * 100 : 0;
                  const label =
                    agent.agent_id === DEFAULT_CHAT_AGENT_ID
                      ? t('DEFAULT_CHAT')
                      : agentNames[agent.agent_id] || getUserLabel(agent.agent_id);
                  return (
                    <tr key={agent.agent_id || `unknown-agent-${index}`} className="border-t border-border">
                      <td className="py-3 pr-4 text-sm text-foreground">{label}</td>
                      <td className="px-4 py-3 font-mono text-xs text-foreground">
                        {formatNumber(agent.turn_count)}
                      </td>
                      <td className="px-4 py-3 font-mono text-xs text-primary dark:text-true-white">
                        {formatNumber(agent.input_tokens)}
                      </td>
                      <td className="px-4 py-3 font-mono text-xs text-success-green">
                        {formatNumber(agent.output_tokens)}
                      </td>
                      <td className="px-4 py-3 font-mono text-xs text-foreground">
                        {formatNumber(agent.total_tokens)}
                      </td>
                      <td className="py-3 pl-4 font-mono text-lg text-warning-high-emphasis">
                        {share.toFixed(1)}%
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export function UsageUserTable({
  users,
  totalTokens,
}: {
  users: TokenUsageUserEntry[];
  totalTokens: number;
}) {
  const { t } = useTranslation();
  const userIds = useMemo(
    () => users.map((user) => user.user_id).filter((userId): userId is string => !!userId),
    [users]
  );
  const userNames = useUsageUserNames(userIds);

  if (!users.length) return null;

  return (
    <div className="space-y-4">
      <UsageSectionLabel>{t('ANALYTICS_PER_USER')}</UsageSectionLabel>
      <Card className="border-border/70 shadow-none">
        <CardContent className="p-5 md:p-6">
          <div className="max-h-[552px] overflow-auto">
            <table className="min-w-full border-collapse">
              <thead>
                <tr className="h-12 border-b border-border">
                  <th className="sticky top-0 z-10 align-middle bg-card pr-4 text-left text-xs font-medium text-muted-foreground">
                    {t('USER')}
                  </th>
                  <th className="sticky top-0 z-10 align-middle bg-card px-4 text-left text-xs font-medium text-muted-foreground">
                    {t('TURNS')}
                  </th>
                  <th className="sticky top-0 z-10 align-middle bg-card px-4 text-left text-xs font-medium text-muted-foreground">
                    {t('INPUT_TOKENS')}
                  </th>
                  <th className="sticky top-0 z-10 align-middle bg-card px-4 text-left text-xs font-medium text-muted-foreground">
                    {t('OUTPUT_TOKENS')}
                  </th>
                  <th className="sticky top-0 z-10 align-middle bg-card px-4 text-left text-xs font-medium text-muted-foreground">
                    {t('TOTAL_TOKENS')}
                  </th>
                  <th className="sticky top-0 z-10 align-middle bg-card pl-4 text-left text-xs font-medium text-muted-foreground">
                    {t('SHARE')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {users.map((user, index) => {
                  const share = totalTokens ? (user.total_tokens / totalTokens) * 100 : 0;
                  return (
                    <tr key={user.user_id ?? `unknown-user-${index}`} className="border-t border-border">
                      <td className="py-3 pr-4 text-sm text-foreground">
                        {user.user_id
                          ? userNames[user.user_id] || getUserLabel(user.user_id)
                          : t('UNKNOWN')}
                      </td>
                      <td className="px-4 py-3 font-mono text-xs text-foreground">
                        {formatNumber(user.turn_count)}
                      </td>
                      <td className="px-4 py-3 font-mono text-xs text-primary dark:text-true-white">
                        {formatNumber(user.input_tokens)}
                      </td>
                      <td className="px-4 py-3 font-mono text-xs text-success-green">
                        {formatNumber(user.output_tokens)}
                      </td>
                      <td className="px-4 py-3 font-mono text-xs text-foreground">
                        {formatNumber(user.total_tokens)}
                      </td>
                      <td className="py-3 pl-4 font-mono text-lg text-warning-high-emphasis">
                        {share.toFixed(1)}%
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
